import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';

const STACK_SIZE = 10;

export interface UndoableEntry {
  /** Stable id used by the toast and floating list. */
  id: string;
  /** Short user-facing label, e.g. "3 Inserate ausgeblendet". */
  label: string;
  /** Timestamp in ms — used to show "Vor 5s". */
  timestamp: number;
  /** Logical handle so the floating-button list can re-issue the undo. */
  invokerKey: UndoInvokerKey;
}

/** Tagged union pointing to a registered undo handler. */
export type UndoInvokerKey =
  | { type: 'softDelete'; entity: import('../types').EntityType; ids: number[] };

type UndoHandler = () => Promise<void>;

interface UndoContextValue {
  stack: UndoableEntry[];
  canUndo: boolean;
  /** Push a new undoable. The toast will appear immediately. */
  pushUndoable: (entry: { label: string; invokerKey: UndoInvokerKey; undo: UndoHandler }) => void;
  /** Pop and run the most recent undo. */
  undoLast: () => Promise<void>;
  /** Pop and run a specific entry by id (from the floating list). */
  undoById: (id: string) => Promise<void>;
  /** The most recently pushed entry — bound to the toast. Resets after dismiss. */
  latestToast: UndoableEntry | null;
  dismissToast: () => void;
  /** Whether the undo subsystem is available for the current user. */
  enabled: boolean;
}

const UndoContext = createContext<UndoContextValue | null>(null);

export function UndoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Undo is only meaningful for users bound to a city (city_admin, member, read_only).
  // system_admin has no city tenant, can't soft-delete, must not see another user's stack.
  const enabled = !!user && user.city_id !== null;

  const [stack, setStack] = useState<UndoableEntry[]>([]);
  const [latestToast, setLatestToast] = useState<UndoableEntry | null>(null);
  const handlerRegistry = useRef<Map<string, UndoHandler>>(new Map());

  // Clear the entire stack whenever the authenticated user changes (login,
  // logout, or switching accounts). The undo history is strictly per-session
  // per-user and never persists.
  useEffect(() => {
    setStack([]);
    setLatestToast(null);
    handlerRegistry.current.clear();
  }, [user?.id]);

  const pushUndoable = useCallback<UndoContextValue['pushUndoable']>(({ label, invokerKey, undo }) => {
    if (!enabled) return;
    const entry: UndoableEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      label,
      timestamp: Date.now(),
      invokerKey,
    };
    handlerRegistry.current.set(entry.id, undo);
    setStack((prev) => [entry, ...prev].slice(0, STACK_SIZE));
    setLatestToast(entry);
  }, [enabled]);

  const runUndo = useCallback(async (id: string) => {
    const handler = handlerRegistry.current.get(id);
    if (handler) {
      try {
        await handler();
      } finally {
        handlerRegistry.current.delete(id);
      }
    }
    setStack((prev) => prev.filter((e) => e.id !== id));
    setLatestToast((cur) => (cur?.id === id ? null : cur));
  }, []);

  const undoLast = useCallback(async () => {
    const top = stack[0];
    if (!top) return;
    await runUndo(top.id);
  }, [stack, runUndo]);

  const undoById = useCallback(async (id: string) => {
    await runUndo(id);
  }, [runUndo]);

  const dismissToast = useCallback(() => setLatestToast(null), []);

  // Global Ctrl/Cmd+Z listener — skipped while user types into an input/textarea
  // so we don't interfere with native browser text undo.
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const target = e.target as HTMLElement | null;
      if (target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      )) return;
      if (!stack[0]) return;
      e.preventDefault();
      undoLast();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [stack, undoLast, enabled]);

  const value = useMemo<UndoContextValue>(() => ({
    stack,
    canUndo: enabled && stack.length > 0,
    pushUndoable,
    undoLast,
    undoById,
    latestToast,
    dismissToast,
    enabled,
  }), [stack, pushUndoable, undoLast, undoById, latestToast, dismissToast, enabled]);

  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>;
}

export function useUndo(): UndoContextValue {
  const ctx = useContext(UndoContext);
  if (!ctx) throw new Error('useUndo must be used inside <UndoProvider>');
  return ctx;
}
